import { Component, type ReactElement, type ReactNode } from 'react';

import classNames from 'classnames';
import { isEqual, isNumber, uniqBy } from 'lodash-es';

import {
  DEFAULT_DATE_FORMAT,
  DEFAULT_DATE_TIME_FORMAT,
} from '@iconik-io/core/src/date/date_formats';
import { format, formatLocalDate } from '@iconik-io/core/src/date/format';
import { getUserLabel } from '@iconik-io/core/src/get_user_label';
import type { FilesFileSchemaWithId } from '@iconik-io/iconik-api/src/services/files';
import type { ObjectType } from '@iconik-io/iconik-api/src/types/object_types';
import {
  FIELD_TYPES,
  type FieldType,
  STATUS_MAP,
} from '@iconik-io/shared-lib/src/constants/search';
import { isSearchItemEntity } from '@iconik-io/shared-lib/src/redux/entities/normalized/search_item';
import { updateEntities } from '@iconik-io/shared-lib/src/redux/entities/slice';
import { getProxyTimecodeOptions } from '@iconik-io/shared-lib/src/utils/get_proxy_timecode_options';
import { rangeToSmpteString } from '@iconik-io/shared-lib/src/utils/range_to_smpte_string';

import ArrowOpener from '../../components/arrow_opener/arrow_opener';
import ApprovalDetailedStatusIcon from '../../components/asset_approval_status_detailed/asset_approval_status_detailed';
import { AnalyzeStatusIcon } from '../../components/asset_status_icon/asset_status_icon';
import DetailedStatusIcon from '../../components/asset_status_icon_detailed/asset_status_icon_detailed';
import AsyncUser from '../../components/async_user/async_user';
import DebugDumpEntity from '../../components/debug_dump_entity/debug_dump_entity';
import Ellipsis from '../../components/ellipsis/ellipsis';
import { FavouriteButton } from '../../components/favourite_button/favourite_button';
import { Keyframes } from '../../components/keyframes/keyframes';
import { Link } from '../../components/link/link';
import { ObjectIcon } from '../../components/object_icon/object_icon';
import SearchItemsThumbnail from '../../components/search_items/search_items_thumbnail/search_items_thumbnail';
import StorageNames from '../../components/table_custom_columns/storage_names/storage_names';
import TooltipTrigger from '../../components/tooltip_trigger/tooltip_trigger';
import UserBubble from '../../components/user_bubble/user_bubble';
import { modifyBranchByID } from '../../ducks/tree/thunks';
import Icon, { ICONS } from '../../icons/icon';
import { useAppDispatch } from '../../store/react_redux_typed';
import type { AnyObject } from '../../types/common';
import type { KeyframeWithId } from '../../types/files';
import { filesize } from '../../utils/filesize';
import numberFormatter from '../../utils/number_formatter';
import { parseObjectIconInfo } from '../../utils/parse_object_icon_info';
import Label from '../label/label';
import Highlight from './highlight';
import OpenContextMenuButton from './open_context_menu_button/open_context_menu_button';
import type { Column, DragAndDropVSide } from './types';

import styles from './table.module.css';

type CellProps<Item> = {
  id: string;
  type: FieldType;
  name: string;
  highlight: string;
  link?: string;
  onTitleLinkClick?: (item: Item, event: React.MouseEvent) => void;
  value: any;
  dateFormat?: string;
  dateTimeFormat?: string;
  item: Item;
  waiting?: boolean;
  keyframesUpdate?: KeyframeWithId[] | undefined;
};

const Cell = <Item,>(props: CellProps<Item>) => {
  let content: ReactNode = null;
  const { type, name, highlight, link, id } = props;
  const dispatch = useAppDispatch();
  // Elastic values come as arrays
  const value = Array.isArray(props.value) ? props.value[0] : props.value;
  const searchItem = isSearchItemEntity(props.item) ? props.item : undefined;
  if (value) {
    switch (type) {
      case FIELD_TYPES.URL:
        if (value) {
          content = (
            <a href={value} rel="noopener noreferrer" target="_blank">
              {value}
            </a>
          );
        }
        break;
      case FIELD_TYPES.BOOLEAN:
        content = (
          <Icon
            className={styles.boolean_icon}
            icon={value ? ICONS.Tick : ICONS.CrossMark}
          />
        );
        break;
      case FIELD_TYPES.EMAIL:
      case FIELD_TYPES.STRINGEXACT:
      case FIELD_TYPES.STRING:
        content = (
          <Ellipsis componentProps={{ className: styles.string }}>
            {highlight ? <Highlight value={highlight} /> : undefined}
            {value && !highlight ? value : ''}
          </Ellipsis>
        );
        break;
      case FIELD_TYPES.TEXT:
        content = (
          <Ellipsis componentProps={{ className: styles.wrap_text }}>
            {highlight ? <Highlight value={highlight} /> : undefined}
            {value && !highlight ? value : ''}
          </Ellipsis>
        );
        break;
      case FIELD_TYPES.INTEGER:
      case FIELD_TYPES.FLOAT:
        content = isNumber(value) ? numberFormatter.format(value) : value;
        break;
      case FIELD_TYPES.TITLE: {
        const proxy = searchItem?.proxies?.[0];

        let subclipTimecode;
        if (searchItem?.type === 'SUBCLIP' && proxy) {
          const timecodeOptions = getProxyTimecodeOptions(proxy);
          subclipTimecode = rangeToSmpteString({
            startMilliseconds: searchItem.time_start_milliseconds,
            endMilliseconds: searchItem.time_end_milliseconds,
            timecodeOptions,
          });
        }
        content = (
          <>
            {searchItem && (
              <DebugDumpEntity id={searchItem.id} entityKey="search_items" />
            )}
            <div className={styles.title_with_version}>
              <div className={styles.title_container}>
                <Ellipsis
                  component={link ? Link : 'span'}
                  componentProps={{
                    to: link ?? '',
                    className: styles.title_ellipsis,
                    onClick: (event) => {
                      props.onTitleLinkClick?.(props.item, event);
                    },
                  }}
                >
                  {highlight ? <Highlight value={highlight} /> : value}
                </Ellipsis>
                {subclipTimecode && (
                  <div className={styles.timecode}>{subclipTimecode}</div>
                )}
              </div>
              {searchItem && (
                <FavouriteButton
                  id={searchItem.id}
                  title={searchItem.title}
                  objectType={searchItem.object_type}
                  favoured={Boolean(searchItem.favoured)}
                  onClick={(favoured) => {
                    dispatch(
                      updateEntities({
                        search_items: {
                          [searchItem.id]: {
                            favoured,
                          },
                        },
                      }),
                    );
                    if (searchItem.object_type === 'collections') {
                      dispatch(
                        modifyBranchByID('COLLECTION', searchItem.id, {
                          favoured,
                        }),
                      );
                    }
                  }}
                />
              )}
            </div>
          </>
        );
        break;
      }
      case FIELD_TYPES.TITLE_WITH_VERSION: {
        content = (
          <>
            {searchItem && (
              <DebugDumpEntity id={searchItem.id} entityKey="search_items" />
            )}
            <div className={styles.title_with_version}>
              <Ellipsis
                component={link ? Link : 'span'}
                componentProps={{
                  to: link ?? '',
                  onClick: (event) => {
                    props.onTitleLinkClick?.(props.item, event);
                  },
                }}
              >
                {highlight ? <Highlight value={highlight} /> : value}
              </Ellipsis>
              {searchItem?.versions && searchItem.versions.length > 1 ? (
                <div className={styles.version}>
                  v.
                  {searchItem.versions.length}
                </div>
              ) : null}
            </div>
          </>
        );
        break;
      }
      case FIELD_TYPES.DATETIME:
      case FIELD_TYPES.DATE: {
        const dateValue = Array.isArray(value) ? value[0] : value;
        if (dateValue) {
          const formatString =
            type === FIELD_TYPES.DATE
              ? (props.dateFormat ?? DEFAULT_DATE_FORMAT)
              : (props.dateTimeFormat ?? DEFAULT_DATE_TIME_FORMAT);

          // Metadata date time values are represented as is.
          // Other date time values are represented in a local user's timezone.
          content = name.startsWith('metadata.')
            ? format(new Date(dateValue), formatString)
            : formatLocalDate(dateValue, formatString);
        }
        break;
      }
      case FIELD_TYPES.DROP_DOWN:
      case FIELD_TYPES.TAG_CLOUD:
        content = (
          <Ellipsis componentProps={{ className: styles.wrap_tags }}>
            {Array.isArray(props.value) && props.value.join(', ')}
          </Ellipsis>
        );
        break;
      case FIELD_TYPES.APPROVAL:
        content = <ApprovalDetailedStatusIcon approval={value} />;
        break;
      case FIELD_TYPES.ANALYZE:
        content = (
          <div className={styles.analyzed}>
            <AnalyzeStatusIcon status={value} />
          </div>
        );
        break;
      case FIELD_TYPES.USER:
        content = (
          <AsyncUser component="div" id={value} className={styles.user}>
            {({ user }): ReactElement =>
              user.id ? (
                <UserBubble
                  size="sm"
                  src={user.photo}
                  name={getUserLabel(user)}
                />
              ) : (
                <div style={{ height: 35, width: 1 }} />
              )
            }
          </AsyncUser>
        );
        break;

      case FIELD_TYPES.STORAGE: {
        const filesValue: FilesFileSchemaWithId[] = Array.isArray(props.value)
          ? props.value
          : [];
        const files = uniqBy(
          filesValue,
          (file) => `${file.format_id}_${file.storage_id}`,
        );
        content = (
          <div className={styles.wrap_tags}>
            <StorageNames files={files} />
          </div>
        );
        break;
      }
      case FIELD_TYPES.FILE_SIZE: {
        const fileSize =
          value.size && value.size > -1 ? filesize(value.size) : 'N/A';
        content = (
          <div className={styles.wrap_tags}>
            <span>{fileSize}</span>
          </div>
        );
        break;
      }
      case FIELD_TYPES.STATUS: {
        const statusMap = STATUS_MAP[name] ? STATUS_MAP[name][value] : false;
        if (statusMap) {
          content = (
            <Label variant={statusMap.bsStyle}>{statusMap.label}</Label>
          );
        }
        break;
      }
      case FIELD_TYPES.OPENER:
        content = <ArrowOpener id={id} />;
        break;
      case FIELD_TYPES.MENU:
        content = <OpenContextMenuButton />;
        break;
      default:
        break;
    }
  }

  // parseObjectIconInfo memoizes a single entry, so it stays inside the
  // branches that need it rather than running for every cell.
  if (searchItem) {
    switch (type) {
      case FIELD_TYPES.THUMBNAIL: {
        const iconInfo = parseObjectIconInfo(searchItem);
        if (
          'type' in iconInfo &&
          ['video', 'subclip'].includes(iconInfo.type)
        ) {
          content = (
            <div className={styles.thumbnail_wrapper}>
              <Keyframes
                searchItem={searchItem}
                size={40}
                imageScrub="auto"
                isThumbnail
                showWarningIcon
              />
            </div>
          );
        } else {
          content = (
            <SearchItemsThumbnail
              value={props.keyframesUpdate || props.value}
              item={searchItem}
              showWarningIcon
            />
          );
        }
        break;
      }
      case FIELD_TYPES.MEDIA_ICON_WITH_STATUS: {
        const iconInfo = parseObjectIconInfo(searchItem);
        content = (
          <span className={styles.status_container}>
            <DetailedStatusIcon object={searchItem} />
            <TooltipTrigger
              tooltipText={'type' in iconInfo ? iconInfo.type : undefined}
              placement="bottom"
              key="media-type"
              componentProps={{
                role: 'tooltip',
                'aria-label': 'Media type',
              }}
            >
              <ObjectIcon
                className={styles.media_icon}
                info={iconInfo}
                size={28}
              />
            </TooltipTrigger>
          </span>
        );
        break;
      }
      case FIELD_TYPES.MEDIA_ICON:
        content = (
          <span className={styles.status_container}>
            <ObjectIcon info={parseObjectIconInfo(searchItem)} size={28} />
          </span>
        );
        break;
      default:
        break;
    }
  }

  return content;
};

export type TableRowProps<T> = {
  ariaLabel?: string;
  dateFormat?: string;
  dateTimeFormat?: string;
  id: string;
  isSelected: boolean;
  isDisabled: boolean;
  isDropTarget: boolean;
  renderColumns: Column<T>[];
  item: T;
  draggable: boolean;
  className?: string;
  cellClassName?: string;
  cellContentClassName?: string;
  getObjectLink?: (
    id: string,
    objectType: ObjectType,
    versionId?: string,
  ) => string;
  onTitleLinkClick?: (item: T, event: React.MouseEvent) => void;
  waiting?: boolean;
  keyframesUpdate: KeyframeWithId[] | undefined;
  dragAndDropVSide?: DragAndDropVSide;
  elementAfterRow?: ReactElement;
  elementAfterCellContent?: ReactElement;
};

class TableRow<T extends AnyObject> extends Component<TableRowProps<T>> {
  static defaultProps = {
    draggable: false,
  };

  shouldComponentUpdate(nextProps: Readonly<TableRowProps<T>>): boolean {
    if (this.props.id === nextProps.id) {
      return !isEqual(this.props, nextProps);
    }

    return false;
  }

  render(): ReactNode {
    const {
      ariaLabel,
      isSelected,
      isDisabled,
      isDropTarget,
      id,
      renderColumns,
      dateFormat,
      dateTimeFormat,
      item,
      draggable,
      className,
      cellClassName,
      cellContentClassName,
      waiting,
      keyframesUpdate,
      elementAfterRow,
      elementAfterCellContent,
      getObjectLink,
      onTitleLinkClick,
    } = this.props;

    let stateClassName: string | undefined;
    if (isDisabled) {
      stateClassName = styles.tr_disabled;
    } else if (isSelected) {
      stateClassName = styles.tr_selected;
    } else if (isDropTarget) {
      stateClassName = styles.tr_droptarget;
    }

    return (
      <>
        <tr
          className={classNames(styles.tr, stateClassName, className)}
          data-id={id}
          data-drop-overlay-id={id}
          draggable={draggable}
          aria-label={ariaLabel}
          aria-selected={isSelected}
        >
          {renderColumns.map((column, index) => {
            const { name, type, formatValue, style, label } = column;
            let content: ReactNode;
            let highlight;
            if (type === FIELD_TYPES.CUSTOM_COMPONENT) {
              const CellComponent = column.component;
              content = <CellComponent item={item} />;
            } else if (type === FIELD_TYPES.CUSTOM_COMPONENT_WITH_PROPS) {
              const CellComponent = column.component;
              content = (
                <CellComponent {...column.componentProps} item={item} />
              );
            } else {
              let value = item[name];
              const namespaceValue = name.split('.');
              const isMetaData = name.startsWith('metadata.');
              const isFiles = name.startsWith('files.');
              const key = `${name}_${id}`;
              const link = getObjectLink?.(id, item.object_type);

              if (typeof formatValue === 'function') {
                value = formatValue(item);
              }

              if (isMetaData && item.metadata) {
                value = item.metadata[namespaceValue[1]];
              }

              if (isFiles && namespaceValue[0] === 'files' && item.files) {
                value = item.files;
              }

              if (item._highlight && item._highlight[name]) {
                highlight = item._highlight[name].join('');
              }
              content = (
                <Cell
                  id={id}
                  value={value}
                  key={key}
                  name={name}
                  type={type}
                  link={link}
                  onTitleLinkClick={onTitleLinkClick}
                  dateFormat={dateFormat}
                  dateTimeFormat={dateTimeFormat}
                  highlight={highlight}
                  item={item}
                  waiting={waiting}
                  keyframesUpdate={keyframesUpdate}
                />
              );
            }

            return (
              <td
                key={`${name}-${index}`}
                className={classNames(styles.td, cellClassName, {
                  [styles.date]: type === 'date',
                })}
                style={style}
                aria-label={label}
              >
                <div
                  className={classNames(
                    styles.cell_content,
                    cellContentClassName,
                    {
                      [styles.expand]: type === 'expand',
                      [styles.date]: type === 'date',
                    },
                  )}
                >
                  {content}
                </div>
                {elementAfterCellContent}
              </td>
            );
          })}
        </tr>
        {elementAfterRow ? (
          <tr
            className={styles.tr}
            data-id={id}
            draggable={draggable}
            aria-hidden="true"
          >
            <td colSpan={renderColumns.length}>{elementAfterRow}</td>
          </tr>
        ) : null}
      </>
    );
  }
}

export default TableRow;
